<script>
  // @ts-check
  import Icon from "../../atoms/Icon.svelte";
  import Markdown from "../../atoms/Markdown.svelte";
  import { createEventDispatcher } from "svelte";

  /** Primarily used for testing
   * @type {string | undefined} */
  export let id = undefined;

  /** @type {string} name of the input field */
  export let name = undefined;

  /** The label for the checkbox
   * @type {string} */
  export let label;

  /** The helper message to display
   * @type {string | undefined} */
  export let helpText = "";

  /** The error message to display
   * @type {string | undefined} */
  export let errorMessage = "";

  /** Indicates whether the value of the input is valid.
   * @type {boolean} */
  export let isValidValue = false;

  /** The `autoComplete` prop determines whether the browser should automatically complete the input value.
   * @type {string | undefined} */
  export let autoComplete = undefined;

  /** Indicates whether the input field is required or not.
   * @type {boolean} */
  export let required = false;

  export let maxlength = 350;
  export let rows = 1;

  /**
   * An optional CSS class to be applied to the component.
   * @type {string}
   */
  export let componentClass = "";

  const transitionClass = "transition-all duration-[400ms] input-easing";

  /** @type {boolean} */
  export let isInvalid = false;

  let defaultValue = "";
  let textareaRef;
  let inputLength = 0;

  let shouldAnimate = false;
  const handleInputChange = (event) => {
    inputLength = event.target.value.length;
    shouldAnimate = true;
    if (inputLength <= 0) {
      shouldAnimate = false;
    }

    if (isInvalid) {
      isInvalid = false;
    }

    // adapt textarea height
    if (textareaRef) {
      if (inputLength > 0) {
        // update
        textareaRef.style.height = `${event.target.scrollHeight}px`;
      } else {
        // reset
        textareaRef.style.height = "auto";
      }
    }
  };

  $: containerClass = isInvalid ? "border-b-[2px] border-system-red" : "border-b border-black";

  $: labelClass = shouldAnimate ? "opacity-[0]" : "opacity-[10]";

  $: animatedLabelClass = shouldAnimate
    ? "opacity-[10] top-[-20px] md:top-[-22px] xl:top-[-27px]"
    : "opacity-[0] top-[-10px] md:top-[-12px] xl:top-[-15px]";

  $: checkSvgAnimateClass = !isInvalid && isValidValue ? `w-5` : "w-0";

  const dispatcher = createEventDispatcher();
</script>

<div class={componentClass} style={"height:auto"}>
  <div class="relative flex w-full items-center border-b-thin {containerClass}">
    <label for={id} class="heading-xs pointer-events-none absolute left-0 top-0 {labelClass}"
      >{label}</label
    >
    <div class="relative mb-3 flex w-full items-center md:mb-4">
      <textarea
        bind:this={textareaRef}
        value={defaultValue}
        name={name || id}
        autocomplete={autoComplete}
        class="heading-xs flex-1 resize-none bg-transparent pr-7 outline-none"
        on:input={handleInputChange}
        on:invalid={(event) => {
          event.preventDefault();
          isInvalid = true;
          dispatcher("invalid", { id });
        }}
        aria-invalid={isInvalid}
        aria-describedby={`${id}-error`}
        on:change
        {id}
        {required}
        {maxlength}
        {rows}
      />
      <div class="heading-xs absolute right-0 top-0 h-[1em] w-[1em]">
        {#if isInvalid}
          <Icon inheritSize iconClass="text-system-red " name="alert" />
        {/if}
      </div>
    </div>

    <span
      aria-hidden="true"
      class="label pointer-events-none absolute left-0 {transitionClass} {animatedLabelClass}"
      >{label}</span
    >

    {#if !isInvalid}
      <div
        class="absolute left-[calc(100%-20px)] top-[0.625rem] overflow-hidden xl:top-4.5 {transitionClass} {checkSvgAnimateClass}"
      >
        <Icon name="check" />
      </div>
    {/if}
  </div>

  <div class="mt-1 flex justify-between gap-4">
    <span>
      {#if helpText}
        <Markdown input={helpText} id="{id}-help-text" class="body-sm font-gotham" />
      {/if}
    </span>
    <span
      id={`${id}-counter`}
      class="label"
      class:text-system-red={inputLength === maxlength}
      role="alert"
    >
      {inputLength}/{maxlength}
    </span>
  </div>

  <span id={`${id}-error`} class="label mt-1 text-system-red" role="alert">
    {#if isInvalid && errorMessage}
      {errorMessage}
    {:else}
      &nbsp <!-- prevent layout shift -->
    {/if}
  </span>
</div>
